import { Component, OnInit } from '@angular/core';
import { DraftUserService, DraftUser, DraftUserRole } from '../../services/draft-user.service';
import { BidItemService, BidItem } from '../../services/bid-item.service';

@Component({
    selector: 'app-draft',
    templateUrl: './draft.component.html',
    styleUrls: ['./draft.component.scss'],
    standalone: false
})
export class DraftComponent implements OnInit {
  username = '';
  loginUuid = '';
  isLoggedIn = false;
  users: DraftUser[] = [];
  role: DraftUserRole = 'Player';
  isAdmin = false;
  currentBidItem: BidItem | null = null;
  allBidItems: BidItem[] = [];
  newBidItemName = '';
  bidAmount = '';
  bidError = '';

  constructor(
    private draftUserService: DraftUserService,
    private bidItemService: BidItemService
  ) {}

  ngOnInit() {
    const storedUsername = localStorage.getItem('draft-username');
    const storedUuid = localStorage.getItem('draft-uuid');
    if (storedUsername && storedUuid) {
      this.username = storedUsername;
      this.loginUuid = storedUuid;
      this.isLoggedIn = true;
    }
    this.draftUserService.getUsers().subscribe(users => {
      this.users = users;
      // Determine admin: first user in the list
      if (this.isLoggedIn && users.length > 0) {
        const adminUser = users[0];
        this.isAdmin = adminUser.uuid === this.loginUuid;
        this.role = this.isAdmin ? 'Admin' : (users.find(u => u.uuid === this.loginUuid)?.role || 'Player');
      }
    });
    this.bidItemService.getCurrentBidItemWithKeys().subscribe(items => {
      this.currentBidItem = items && items.length > 0 ? items[0] : null;
    });
    this.bidItemService.getAllBidItemsWithKeys().subscribe(items => {
      this.allBidItems = items.filter(i => i.completed);
    });
  }

  login() {
    if (this.username.trim()) {
      this.loginUuid = this.generateUuid();
      localStorage.setItem('draft-username', this.username);
      localStorage.setItem('draft-uuid', this.loginUuid);
      this.isLoggedIn = true;
      // Default role is Player, Admin will be set after users are loaded
      const user = new DraftUser(this.username, this.loginUuid, 'Player');
      this.draftUserService.addUser(user);
    }
  }

  setDrafter(uuid: string) {
    if (!this.isAdmin) {
      return;
    }
    // Remove Drafter role from all except Admin
    this.users.forEach(u => {
      if (u.role === 'Drafter' && u.uuid !== uuid && u.uuid !== this.users[0].uuid) {
        u.role = 'Player';
        this.draftUserService.addUser(u);
      }
    });
    const drafterUser = this.users.find(u => u.uuid === uuid);
    if (drafterUser) {
      drafterUser.role = 'Drafter';
      this.draftUserService.addUser(drafterUser);
    }
    // If Admin was Drafter and selects another, revert Admin to Admin
    if (this.isAdmin && uuid !== this.users[0].uuid) {
      const adminUser = this.users[0];
      if (adminUser.role === 'Drafter') {
        adminUser.role = 'Admin';
        this.draftUserService.addUser(adminUser);
      }
    }
  }

  removeDrafter(uuid: string) {
    if (!this.isAdmin) {
      return;
    }
    const user = this.users.find(u => u.uuid === uuid);
    if (user && user.role === 'Drafter') {
      user.role = 'Player';
      this.draftUserService.addUser(user);
    }
  }

  addBidItem() {
    this.bidError = '';
    const drafter = this.users.find(u => u.uuid === this.loginUuid);
    const initialBid = Number(this.bidAmount);
    if (!this.isDrafter() || !this.newBidItemName.trim() || this.currentBidItem) {
      return;
    }
    if (!this.bidAmount || isNaN(initialBid) || !Number.isInteger(initialBid) || initialBid <= 0) {
      this.bidError = 'Enter a valid whole number greater than 0.';
      return;
    }
    if (drafter && initialBid > drafter.wallet) {
      this.bidError = 'You cannot bid more than you have.';
      return;
    }
    const item: BidItem = {
      name: this.newBidItemName,
      drafterUuid: this.loginUuid,
      topBid: initialBid,
      topBidderUuid: this.loginUuid,
      completed: false
    };
    this.bidItemService.addBidItem(item);
    this.newBidItemName = '';
    this.bidAmount = '';
  }

  placeBid() {
    this.bidError = '';
    if (!this.currentBidItem) {
      return;
    }
    const amount = Number(this.bidAmount);
    const user = this.users.find(u => u.uuid === this.loginUuid);
    if (!user) {
      this.bidError = 'User not found.';
      return;
    }
    if (!this.bidAmount || isNaN(amount) || !Number.isInteger(amount) || amount <= this.currentBidItem.topBid) {
      this.bidError = 'Enter a valid whole number greater than the current top bid.';
      return;
    }
    if (amount > user.wallet) {
      this.bidError = 'You cannot bid more than you have.';
      return;
    }
    // Update bid item
    if (this.currentBidItem.id) {
      this.bidItemService.updateBidItem(this.currentBidItem.id, {
        topBid: amount,
        topBidderUuid: this.loginUuid
      });
    }
    this.bidAmount = '';
  }

  closeBid() {
    if (!this.isAdmin || !this.currentBidItem || !this.currentBidItem.id) {
      return;
    }
    this.bidItemService.updateBidItem(this.currentBidItem.id, {
      completed: true,
      purchasedFor: this.currentBidItem.topBid,
      purchasedByUuid: this.currentBidItem.topBidderUuid
    });
    // Remove Drafter role from the current drafter
    const drafter = this.users.find(u => u.role === 'Drafter');
    if (drafter) {
      drafter.role = 'Player';
      this.draftUserService.addUser(drafter);
    }
    // Deduct winning bid from winner's wallet
    if (this.currentBidItem.topBidderUuid && this.currentBidItem.topBid > 0) {
      const winner = this.users.find(u => u.uuid === this.currentBidItem.topBidderUuid);
      if (winner) {
        winner.wallet = Math.max(0, winner.wallet - this.currentBidItem.topBid);
        this.draftUserService.addUser(winner);
      }
    }
  }

  // Fix: isDrafter should return true if the user's role is Drafter in the users array
  isDrafter(): boolean {
    const me = this.users.find(u => u.uuid === this.loginUuid);
    return !!me && me.role === 'Drafter';
  }

  generateUuid(): string {
    // Simple UUID v4 generator (bitwise ops are required for UUID, TSLint warning is safe to ignore here)
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.floor(Math.random() * 16);
      const v = c === 'x' ? r : (r % 4 + 8);
      return v.toString(16);
    });
  }

  getUsername(uuid: string): string {
    const user = this.users.find(u => u.uuid === uuid);
    return user ? user.username : '';
  }

  getWallet(uuid: string): number {
    const user = this.users.find(u => u.uuid === uuid);
    return user ? user.wallet : 0;
  }
}
